import {
  trackResendOTPCode,
  trackVerifyOTPSuccessful,
  trackVerifyOTPWrong,
  trackViewOTPModal,
} from 'actions/tracker/tracker';
import { newNotification } from 'actions/ui/ui';
import type { HttpError } from 'constants/http';
import { AUTOFOCUS_DELAY_MS } from 'constants/timers';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import { getRecipientId } from 'selectors/entities/recipient/recipient';
import { getCurrentCountryCode, getPlatform } from 'selectors/ui/ui';
import type { OtpContext } from 'services/payex/otp/types';
import { useDispatch } from 'store/configureStore';
import { useTranslations } from 'utils/translations/useTranslations';
import { useCountdown } from './useCountdown';
import { useSendOtp } from './useSendOtp';
import { useVerifyOtp } from './useVerifyOtp';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const OTP_VALIDITY_MS = 10 * 60 * 1000; // 10 minutos
const FOCUS_MAX_ATTEMPTS = 5;

let lastOtpSent: { email: string; context: string; timestamp: number } | null =
  null;

export const resetOtpCache = () => {
  lastOtpSent = null;
};

type OtpApiError = {
  type: string;
  message: string;
  attemptsRemaining?: number;
};

const parseOtpError = (error: HttpError): OtpApiError | null => {
  const errors = error.parsedBody?.errors as OtpApiError[] | undefined;
  return errors?.[0] ?? null;
};

type UseOtpFlowParams = {
  isOpen: boolean;
  email: string;
  senderName?: string;
  recipientName?: string;
  context: OtpContext;
  onVerified: () => void;
};

const useOtpFlow = ({
  isOpen,
  email,
  senderName,
  recipientName,
  context,
  onVerified,
}: UseOtpFlowParams) => {
  const i18n = useTranslations();
  const dispatch = useDispatch();
  const sendOtp = useSendOtp();
  const verifyOtp = useVerifyOtp();

  const platform = useSelector(getPlatform);
  const recipientId = useSelector(getRecipientId);
  const countryFrom = useSelector(getCurrentCountryCode) as string;
  const countryTo = useSelector(
    (state: RootState) => state.entities?.recipient?.country,
  );

  const pinFieldRef = useRef<HTMLDivElement>(null);

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const { countdown: resendCooldown, start: startResendCooldown } =
    useCountdown();

  const isLoading = sendOtp.isPending || verifyOtp.isPending;

  const buildSendOtpPayload = useCallback(() => {
    const payload = {
      email,
      senderName,
      context,
      platform,
      ...(recipientId && { portalCode: recipientId }),
      ...(countryFrom && { countryFrom }),
      ...(countryTo && { countryTo }),
    };
    if (context === 'payer_information' && recipientName) {
      return { ...payload, recipientName };
    }
    return payload;
  }, [
    email,
    senderName,
    recipientName,
    context,
    platform,
    recipientId,
    countryFrom,
    countryTo,
  ]);

  const focusFirstInput = useCallback(() => {
    const attemptFocus = (retriesLeft = FOCUS_MAX_ATTEMPTS) => {
      const firstInput =
        pinFieldRef.current?.querySelector<HTMLInputElement>('input');

      if (firstInput && document.activeElement !== firstInput) {
        firstInput.focus();

        if (document.activeElement !== firstInput && retriesLeft > 0) {
          setTimeout(() => attemptFocus(retriesLeft - 1), 100);
        }
      }
    };

    setTimeout(() => attemptFocus(), AUTOFOCUS_DELAY_MS);
  }, []);

  const resetState = useCallback(() => {
    setCode('');
    setErrorMessage('');
  }, []);

  const showSendFailedToast = useCallback(() => {
    dispatch(
      newNotification({
        message: 'otp.error.sendFailed',
        type: 'error',
      }),
    );
  }, [dispatch]);

  const showVerifyFailedToast = useCallback(() => {
    dispatch(
      newNotification({
        message: 'otp.error.verifyFailed',
        type: 'error',
      }),
    );
  }, [dispatch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: only fire when modal opens
  useEffect(() => {
    if (isOpen) {
      dispatch(trackViewOTPModal());
      resetState();
      focusFirstInput();

      const now = Date.now();
      const shouldSkipSend =
        lastOtpSent &&
        lastOtpSent.email === email &&
        lastOtpSent.context === context &&
        now - lastOtpSent.timestamp < OTP_VALIDITY_MS;

      if (shouldSkipSend) {
        return;
      }

      sendOtp.mutate(buildSendOtpPayload(), {
        onSuccess: () => {
          lastOtpSent = { email, context, timestamp: Date.now() };
        },
        onError: (error: HttpError) => {
          if (error.status === 429) {
            dispatch(
              newNotification({
                message: 'otp.error.rateLimitExceeded',
                type: 'error',
              }),
            );
            return;
          }
          showSendFailedToast();
        },
      });
    }
  }, [isOpen]);

  const handleVerify = () => {
    if (code.length !== OTP_LENGTH) {
      setErrorMessage(i18n.t('otp.error.required'));
      return;
    }

    setErrorMessage('');
    verifyOtp.mutate(
      { email, code },
      {
        onSuccess: () => {
          dispatch(trackVerifyOTPSuccessful());
          onVerified();
        },
        onError: (error: HttpError) => {
          const otpError = parseOtpError(error);
          if (!otpError) {
            showVerifyFailedToast();
            setCode('');
            focusFirstInput();
            return;
          }

          dispatch(trackVerifyOTPWrong());
          setErrorMessage(i18n.t('otp.error.invalid'));
          setCode('');
          focusFirstInput();
        },
      },
    );
  };

  const handleResend = () => {
    if (resendCooldown > 0) {
      return;
    }

    resetState();
    startResendCooldown(RESEND_COOLDOWN_SECONDS);

    sendOtp.mutate(buildSendOtpPayload(), {
      onSuccess: () => {
        dispatch(trackResendOTPCode());
        lastOtpSent = { email, context, timestamp: Date.now() };
      },
      onError: (error: HttpError) => {
        if (error.status === 429) {
          dispatch(
            newNotification({
              message: 'otp.error.rateLimitExceeded',
              type: 'error',
            }),
          );
          return;
        }
        showSendFailedToast();
      },
    });
  };

  return {
    code,
    setCode,
    errorMessage,
    isLoading,
    resendCooldown,
    pinFieldRef,
    handleVerify,
    handleResend,
  };
};

export { useOtpFlow };
